import React, { useContext, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import { useEffectOnMount } from '../../../../hooks/useEffectOnMount';
import RouteContext from '../../../../routes/context';
import {
  productPriceClick,
  productClickOutBounce,
} from '../../actions/product';
import { AppState } from '../../../../reducers';
import { getPriceFilters } from './utils';
import { trackPriceListFilterUpdate } from '../tracking';
import PriceListPanel from './price-list-panel';
import { PriceListProps, PriceListStateProps } from './types';
import { PriceListFilterContext } from '../../price-list-filter-context';
import ProductFilters from '../product-filters';

const PriceList: React.FC<PriceListProps> = props => {
  const {
    id,
    category,
    me,
    prices,
    dispatchProductPriceClick,
    variantGroups,
    conditions,
    productConditionVisible,
    ...priceListPanelProps
  } = props;

  const { t, market } = useContext(AppContext);
  const { dispatch: routeDispatch } = useContext(RouteContext);
  const {
    filters,
    setFilters,
    selectedVariant,
    selectedCondition,
  } = useContext(PriceListFilterContext);

  const [featuredAlternativePriceType] = useState<string>(
    defaultFeaturedAlternativePriceType(market)
  );

  const [hiddenListOptions] = useState<string[]>(
    defaultHiddenListOptions(market)
  );

  const priceFilters = getPriceFilters(
    prices,
    t,
    filters,
    featuredAlternativePriceType,
    hiddenListOptions,
    productConditionVisible
  );

  const quickFilters = priceFilters.filter(elem => elem.showAsQuickFilter);

  const handlePriceFilterCheckboxChange = useCallback(
    e => {
      const isQuickFilter = Boolean(
        quickFilters.filter(quickFilter => quickFilter.name === e.target.name)
          .length
      );

      const selectedFilter = priceFilters.find(
        priceFilter => priceFilter.name === e.target.name
      );

      if (selectedFilter && selectedFilter.type)
        trackPriceListFilterUpdate(selectedFilter.type, isQuickFilter);

      routeDispatch({
        type: 'PRICE_FILTER',
        options: {
          ...filters,
          [e.target.name]: e.target.checked,
        },
        // selectedVariant.id === 0 corresponds to "all", which should not be fetched by id
        variants:
          selectedVariant && selectedVariant.id !== 0
            ? [selectedVariant.id]
            : null,
        condition: selectedCondition,
      });

      setFilters({
        ...filters,
        [e.target.name]: e.target.checked,
      });
    },
    [
      filters,
      priceFilters,
      routeDispatch,
      quickFilters,
      selectedVariant,
      selectedCondition,
      setFilters,
    ]
  );

  useEffectOnMount(() => {
    if (!prices?.nodes?.length) {
      // If initial fetch returns no prices, fetch prices again with showUsedProducts: true
      routeDispatch({
        type: 'PRICE_FILTER',
        options: {
          ...filters,
        },
        variants: null,
        condition: null,
      });

      setFilters({
        ...filters,
      });
    }
  });

  return (
    <>
      <ProductFilters
        variantGroups={variantGroups}
        conditions={productConditionVisible ? conditions : []}
        categoryId={category?.id}
      />
      <PriceListPanel
        me={me}
        productId={id}
        handlePriceFilterCheckboxChange={handlePriceFilterCheckboxChange}
        priceFilters={priceFilters}
        quickFilters={quickFilters}
        showProductInformationTexts={market.showProductInformationTexts}
        prices={prices}
        storeStatistics={prices?.meta?.storeStatistics}
        dispatchProductPriceClick={dispatchProductPriceClick}
        category={category}
        {...priceListPanelProps}
      />
    </>
  );
};

const defaultHiddenListOptions = (market: ClientMarket) => {
  return market.priceList && market.priceList.hiddenListOptions
    ? [...market.priceList.hiddenListOptions]
    : [];
};

const defaultFeaturedAlternativePriceType = (market: ClientMarket) => {
  return market.priceList && market.priceList.featuredAlternativePriceType
    ? market.priceList.featuredAlternativePriceType
    : 'international';
};

const mapStateToProps = (state: AppState): PriceListStateProps => ({
  me: state.areas.account.me,
  currentSort: state.areas.product.sort,
  currentGrouping: state.areas.product.grouping,
});

const dispatchProps = {
  dispatchProductPriceClick: productPriceClick,
  dispatchProductClickOutBounce: productClickOutBounce,
};

export default connect(mapStateToProps, dispatchProps)(PriceList);
